import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as sessionService from '../services/sessionService';
import { PhaseComponentFactory } from '../components/phases/PhaseComponentFactory';
import { SessionTree } from '../components/sessions/SessionTree';
import { ProcessingGraph } from '../components/graphs/ProcessingGraph';
import { CheckpointTimeline } from '../components/sessions/CheckpointTimeline';
import { CheckpointControls } from '../components/sessions/CheckpointControls';
import { SessionForkButton } from '../components/sessions/SessionForkButton';
import websocketService from '../services/websocketService';
import { useTheme } from '../contexts/ThemeContext';
import { LlmDataWarning } from '../components/common/LlmDataWarning';
import SessionSidebar from '../components/sessions/SessionSidebar';

export const SessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [wsConnected, setWsConnected] = useState(false);
  const [showSessionTree, setShowSessionTree] = useState(false);
  const [showProcessingGraph, setShowProcessingGraph] = useState(false);
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<string | null>(null);
  
  const { 
    data: session, 
    isLoading: sessionLoading, 
    error: sessionError,
    refetch: refetchSession
  } = useQuery(
    ['session', sessionId],
    () => sessionId ? sessionService.getSession(sessionId) : Promise.reject('No session ID'),
    { enabled: !!sessionId }
  );
  
  const { 
    data: phaseState, 
    isLoading: phaseLoading, 
    error: phaseError,
    refetch: refetchPhaseState
  } = useQuery(
    ['phaseState', sessionId, session?.phase],
    () => sessionId && session?.phase 
      ? sessionService.getPhaseState(sessionId, session.phase) 
      : Promise.reject('No session or phase'),
    { enabled: !!sessionId && !!session?.phase }
  );
  
  // Connect to WebSocket when session ID is available
  useEffect(() => {
    if (!sessionId) return;
    
    // Define message handlers
    const handleSessionState = (data: any) => {
      console.log('Received session state update:', data);
      refetchSession();
    };
    
    const handlePhaseUpdate = (data: any) => {
      console.log('Received phase update:', data);
      refetchPhaseState();
    };
    
    const handlePhaseTransition = (data: any) => {
      console.log('Received phase transition:', data);
      refetchSession();
      refetchPhaseState();
    };
    
    // Register handlers
    websocketService.on('session_state', handleSessionState);
    websocketService.on('phase_update', handlePhaseUpdate);
    websocketService.on('phase_transition', handlePhaseTransition);
    
    // Connect to WebSocket
    websocketService.connect(sessionId)
      .then(() => {
        setWsConnected(true);
        console.log('WebSocket connected');
      })
      .catch(err => {
        console.error('WebSocket connection failed:', err);
      });
    
    // Clean up WebSocket connection and handlers on unmount
    return () => {
      // First remove all event handlers
      websocketService.off('session_state', handleSessionState);
      websocketService.off('phase_update', handlePhaseUpdate);
      websocketService.off('phase_transition', handlePhaseTransition);
      
      // Then disconnect with a small delay to ensure handlers are removed first
      setTimeout(() => {
        websocketService.disconnect();
      }, 0);
    };
  }, [sessionId, refetchSession, refetchPhaseState]);
  
  const handlePhaseAction = async (action: string, data: any = {}) => {
    console.log(`SessionDetail: Handling phase action ${action}`, data);
    if (!sessionId || !session?.phase) {
      console.error('Cannot execute action: sessionId or phase is missing', { sessionId, phase: session?.phase });
      return;
    }
    
    try {
      console.log(`Executing action ${action} for phase ${session.phase} in session ${sessionId}`);
      const result = await sessionService.executePhaseAction(sessionId, session.phase, action, data);
      console.log(`Action result:`, result);
      
      // Manually refetch to ensure we have the latest state
      setTimeout(() => {
        console.log('Manually refetching session and phase state after action');
        refetchSession();
        refetchPhaseState();
      }, 500);
    } catch (err) {
      console.error('Error executing phase action:', err);
    }
  };
  
  const handleAdvanceSession = async () => {
    console.log(`SessionDetail: Handling advance session for ${sessionId}`);
    if (!sessionId) {
      console.error('Cannot advance session: sessionId is missing');
      return;
    }
    
    try {
      console.log(`Advancing session ${sessionId}`);
      const result = await sessionService.advanceSession(sessionId);
      console.log(`Advance result:`, result);
      
      // Manually refetch to ensure we have the latest state
      setTimeout(() => {
        console.log('Manually refetching session and phase state after advance');
        refetchSession();
        refetchPhaseState();
      }, 500);
    } catch (err) {
      console.error('Error advancing session:', err);
    }
  };
  
  if (sessionLoading) {
    return <div className={`text-center py-10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading session...</div>;
  }
  
  if (sessionError) {
    return <div className={`text-center py-10 text-red-600 ${darkMode ? 'bg-red-900 bg-opacity-20' : ''}`}>Error loading session</div>;
  }
  
  if (!session) {
    return <div className={`text-center py-10 text-red-600 ${darkMode ? 'bg-red-900 bg-opacity-20' : ''}`}>Session not found</div>;
  }
  
  // Check if the phase state indicates the session is not in the workspace
  if (phaseState && phaseState.status === "not_found_in_workspace") {
    return (
      <div className={`text-center py-10 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`text-red-600 mb-4 ${darkMode ? 'text-red-400' : ''}`}>{phaseState.message}</div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          onClick={() => {
            refetchSession();
            refetchPhaseState();
          }}
        >
          Reload Session
        </button>
      </div>
    );
  }
  
  const sessionTitle = session.persona_name
    ? session.persona_name.replace(/_/g, ' ')
    : session.name || `Session ${(session.id || '').slice(0, 8)}`;

  return (
    <div className={`flex gap-0 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
      {/* ── Main Content ── */}
      <div className="flex-1 min-w-0">
      <LlmDataWarning />
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${
            darkMode ? 'from-emerald-400 to-teal-400' : 'from-emerald-600 to-teal-600'
          }`}>
            {sessionTitle}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phase: {session.phase}</span>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Agents: {session.agents?.length || 0}</span>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Artifacts: {session.artifacts?.length || 0}</span>
            {wsConnected && (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Connected
              </span>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <SessionForkButton sessionId={sessionId} />
          <button
            className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center text-sm"
            onClick={() => {
              const workflowsUrl = `/workflows?sessionId=${sessionId}`;
              window.open(workflowsUrl, '_blank');
            }}
          >
            Workflows
          </button>
        </div>
      </div>
      <div className="h-px mb-4 bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
      
      <div className="flex space-x-4 mb-6">
        <button
          className={`px-4 py-2 rounded ${
            showSessionTree 
              ? 'bg-blue-600 text-white' 
              : darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          onClick={() => setShowSessionTree(!showSessionTree)}
        >
          {showSessionTree ? 'Hide Session Tree' : 'Show Session Tree'}
        </button>
        
        <button
          className={`px-4 py-2 rounded ${
            showProcessingGraph 
              ? 'bg-blue-600 text-white' 
              : darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          onClick={() => setShowProcessingGraph(!showProcessingGraph)}
        >
          {showProcessingGraph ? 'Hide Processing Graph' : 'Show Processing Graph'}
        </button>
        
        <button
          className={`px-4 py-2 rounded ${
            showCheckpoints 
              ? 'bg-blue-600 text-white' 
              : darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          onClick={() => setShowCheckpoints(!showCheckpoints)}
        >
          {showCheckpoints ? 'Hide Checkpoints' : 'Show Checkpoints'}
        </button>
      </div>
      
      {showSessionTree && session.lineage?.root_uid && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4 mb-6`}>
          <h2 className="text-xl font-semibold mb-4">Session Lineage</h2>
          <SessionTree rootSessionId={session.lineage.root_uid} height="400px" />
        </div>
      )}
      
      {showProcessingGraph && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4 mb-6`}>
          <h2 className="text-xl font-semibold mb-4">Processing Graph</h2>
          <ProcessingGraph sessionId={sessionId} height="500px" />
        </div>
      )}
      
      {showCheckpoints && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <CheckpointTimeline 
              sessionId={sessionId} 
              onCheckpointSelect={setSelectedCheckpoint} 
            />
          </div>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4`}>
            {selectedCheckpoint ? (
              <>
                <h2 className="text-xl font-medium mb-4">Checkpoint Actions</h2>
                <CheckpointControls 
                  sessionId={sessionId} 
                  checkpointId={selectedCheckpoint} 
                />
              </>
            ) : (
              <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Select a checkpoint to view actions
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-6`}>
        <h2 className="text-xl font-semibold mb-4">Current Phase: {session.phase}</h2>
        
        {(() => {
          console.log('Phase loading state:', { phaseLoading, phaseError, phaseState });
          
          if (phaseLoading) {
            return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading phase state...</div>;
          }
          
          if (phaseError) {
            console.error('Phase error:', phaseError);
            return <div className={`text-center py-4 text-red-600 ${darkMode ? 'text-red-400' : ''}`}>Error loading phase state</div>;
          }
          
          if (!phaseState) {
            return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No phase state available</div>;
          }
          
          try {
            return (
              <PhaseComponentFactory 
                phase={session.phase}
                state={phaseState}
                onAction={handlePhaseAction}
              />
            );
          } catch (error) {
            console.error('Error rendering phase component:', error);
            return (
              <div className="text-center py-4 text-red-600">
                Error rendering phase component. See console for details.
              </div>
            );
          }
        })()}
      </div>
      
      <div className="flex justify-end">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          onClick={handleAdvanceSession}
        >
          Advance Session
        </button>
      </div>
      </div>{/* end main content */}

      {/* ── Context Sidebar ── */}
      <SessionSidebar session={{
        ...session,
        message_count: session.message_count || session.messages?.length || 0,
        artifacts: session.artifacts || [],
      }} />
    </div>
  );
};
